import { useLDClient } from '@afterpaytouch/feature'
import { useAppDispatch } from '../hooks'
import { setFeatureFlag } from './reducer'
import { Experiment, Experiments, KnownExperiment } from '../../model'
import { useExperiment } from '../experiments/hooks'
import { useConsumerSelector, useFeatureFlagsState } from '..'

/**
 * Generic hook for cached feature flags.
 *
 * Returns `defaultValue` while LaunchDarkly is identifying the current user. Once LD is
 * ready, the hook checks for a cached value in Redux; if found, returns it immediately.
 * Otherwise, it reads the LaunchDarkly variation directly and writes it into the cache
 * only when LD has resolved the flag for the current user.
 *
 * The cache write requires two conditions:
 *
 * 1. `client.variation(key) !== undefined` — LD has loaded *some* value for this flag. We
 *    bypass `useFlag(key, defaultValue)` because it masks unresolved variations with the
 *    supplied default, which made it impossible to distinguish "LD hasn't resolved yet" from
 *    "LD resolved to false/0/empty". An earlier implementation cached that masked default and
 *    then short-circuited every later read, pinning the flag to its default.
 *
 * 2. `hasFeatureFlagsReady === true` — LaunchDarkly has identified the *current* user (the
 *    `onDone` callback in `LaunchDarklyContext` has fired). LD bootstraps from localStorage
 *    (`bootstrap: 'localStorage'` in `DEFAULT_LAUNCH_DARKLY_CONFIG`), so before identify
 *    completes `client.variation()` can return a *previous* user's flag values. Caching those
 *    would persist the wrong gate for the rest of the session.
 *
 * @example
 * // Boolean flag (default behavior)
 * const isEnabled = useCachedFeatureFlag('my-boolean-flag')
 *
 * // String array flag
 * const sizes = useCachedFeatureFlag<string[]>('checkout-consistent-size', [])
 *
 * // String flag with default
 * const text = useCachedFeatureFlag<string>('maintenance-mode-text', 'Default text')
 *
 * // Number flag
 * const amount = useCachedFeatureFlag<number>('pay-in-2-amount', 0)
 *
 * @param key - The feature flag key
 * @param defaultValue - Optional default value returned until LD resolves the flag for the
 *   current user
 * @returns The feature flag value
 */
export const useCachedFeatureFlag = <T = boolean>(key: string, defaultValue?: T): T => {
  const dispatch = useAppDispatch()
  const { flags } = useFeatureFlagsState()
  const { hasFeatureFlagsReady } = useConsumerSelector()
  const client = useLDClient()
  const cachedValue = flags[key] as T | undefined

  if (!hasFeatureFlagsReady) {
    return defaultValue as T
  }

  if (cachedValue !== undefined) {
    return cachedValue
  }

  const ldVariation = client?.variation(key) as T | undefined

  if (ldVariation !== undefined) {
    dispatch(setFeatureFlag({ key, value: ldVariation }))
    return ldVariation
  }

  return defaultValue as T
}

/**
 * Per-user kill-switch read: returns `defaultValue` until LaunchDarkly has identified the *current*
 * consumer (`hasFeatureFlagsReady`), then the live LD client variation for the current consumer.
 *
 * Unlike {@link useCachedFeatureFlag}, this deliberately does NOT read or write the persisted
 * (sessionStorage) flag cache. That cache is keyed by flag key only — not by consumer uuid — and is
 * only purged when LaunchDarkly re-identifies, so for a per-uuid rollout/kill switch a value resolved
 * for one consumer can still be read by a different consumer earlier in the same tab. Reading the live
 * variation, gated on readiness, always reflects the current user: hidden (default) until the current
 * consumer's flags resolve, then their own value. Use this for per-user gates; use the cached hook for
 * static/global flags where cross-consumer reuse is harmless and the cache saves LD reads.
 *
 * @param key - The feature flag key
 * @param defaultValue - Value returned until LD has identified the current consumer
 */
export const useReadyFeatureFlag = <T = boolean>(key: string, defaultValue: T): T => {
  const { hasFeatureFlagsReady } = useConsumerSelector()
  const client = useLDClient()
  // Don't evaluate the flag until LD has identified the current consumer. Calling client.variation
  // before then (as useFlag does internally on every render) records an evaluation — LD rollout
  // telemetry + Datadog flag-used — against the stale pre-identify context, i.e. the previous
  // consumer's value. Returning the default until readiness keeps both the UI and the telemetry clean.
  if (!hasFeatureFlagsReady) {
    return defaultValue
  }
  const value = client?.variation(key, defaultValue) as T | undefined
  return value ?? defaultValue
}

/**
 * Generic hook for cached experiment feature flags.
 * First checks for cached value in Redux, if found returns it.
 * If no cached value is found, calls the existing useExperiment hook and caches the result.
 * @param name - The experiment name
 * @param fireEvent - Whether to fire experiment events
 * @returns The experiment object
 */
export const useCachedExperiment = (name: KnownExperiment, fireEvent: boolean = true): Experiment => {
  const dispatch = useAppDispatch()
  const { flags } = useFeatureFlagsState()
  const cachedValue = flags[Experiments[name]] as Experiment | undefined
  const experiment = useExperiment(name, fireEvent)

  // If we have a valid cached value, return it immediately
  if (cachedValue !== undefined) {
    return cachedValue
  }

  // Cache the experiment result when it's not loading
  if (!experiment.loading) {
    dispatch(setFeatureFlag({ key: Experiments[name], value: experiment }))
  }

  return experiment
}
